/**
 * Sync health — is each integration still sending, and what is stuck?
 *
 * Ceno Hub's delivery sync was switched off for six days in October 2026 and
 * this screen said "Active" the whole time, because "Active" was a fact about
 * the credential, not about the deliveries. This panel answers the question an
 * owner actually has: are new orders reaching the books, and if not, which ones
 * are waiting and why.
 *
 * Two lists, because the stuck work lives in two places. "Waiting to send" is
 * what the sending system reported it is still holding — CenoX cannot see those
 * any other way. "Held in CenoX" is what arrived here out of order and is
 * waiting for an earlier event. Both clear by themselves once the cause is
 * fixed; neither needs anybody to re-enter a delivery.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  INTEGRATION_HEALTH_LABELS,
  type IntegrationHealth as Health,
  type IntegrationHealthStatus,
} from '@cenox/shared';
import { Badge, Card, ErrorState } from '../../components/ui';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

const TONE: Record<IntegrationHealthStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  healthy: 'success',
  delayed: 'warning',
  failing: 'danger',
  silent: 'danger',
  not_reporting: 'neutral',
};

/** "3 min ago", because "14:02" makes the reader do arithmetic to see a problem. */
export function ago(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'never';
  const ms = now.getTime() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

function at(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function IntegrationHealth(): ReactNode {
  const path = useBusinessPath();
  const health = useQuery({
    queryKey: ['integrations', 'health', path('')],
    queryFn: () => api.get<{ data: Health[] }>(path('/integrations/health')),
    // Once a minute while the page is open: a stopped sender is the thing
    // somebody is watching for, and they should not have to reload to see it.
    refetchInterval: 60_000,
  });

  if (health.isError) {
    return (
      <ErrorState message="Could not load sync health." onRetry={() => void health.refetch()} />
    );
  }
  const rows = health.data?.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="space-y-5">
      {rows.map((h) => (
        <HealthCard key={h.credentialId} health={h} />
      ))}
    </div>
  );
}

function HealthCard({ health: h }: { health: Health }): ReactNode {
  const waiting = h.report?.items ?? [];
  return (
    <Card>
      <section aria-label={`Sync health for ${h.name}`} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{h.name} — sync health</h3>
            <p className="text-xs text-slate-500">
              Last report {ago(h.lastHeartbeatAt)} · last delivery event {ago(h.lastEventAt)}
            </p>
          </div>
          <Badge tone={TONE[h.status]}>{INTEGRATION_HEALTH_LABELS[h.status]}</Badge>
        </div>

        {h.problems.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            {h.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        {h.report && (
          <dl className="grid grid-cols-2 gap-2 text-xs text-slate-600 sm:grid-cols-4">
            <Stat label="Waiting to send" value={h.report.outbox.pending} />
            <Stat label="Rejected" value={h.report.outbox.failed} />
            <Stat label="Not yet queued" value={h.report.backlog.eligibleUnsent} />
            <Stat label="Held in CenoX" value={h.held.count} />
          </dl>
        )}

        {waiting.length > 0 && (
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Waiting in the sending system
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1.5">Order</th>
                    <th className="py-1.5">Version</th>
                    <th className="py-1.5">Event</th>
                    <th className="py-1.5">State</th>
                    <th className="py-1.5">Attempts</th>
                    <th className="py-1.5">Last error</th>
                    <th className="py-1.5">Next attempt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {waiting.map((e) => (
                    <tr key={`${e.externalRef}:${e.version}`}>
                      <td className="py-1.5 font-medium text-slate-900">
                        {e.label ?? e.externalRef}
                      </td>
                      <td className="py-1.5">v{e.version}</td>
                      <td className="py-1.5 font-mono text-xs">{e.eventType}</td>
                      <td className="py-1.5">
                        <Badge tone={e.status === 'FAILED' ? 'danger' : 'warning'}>
                          {e.status === 'FAILED' ? 'Rejected' : 'Waiting'}
                        </Badge>
                      </td>
                      <td className="py-1.5">{e.attempts}</td>
                      <td className="max-w-64 truncate py-1.5 text-xs text-slate-500" title={e.lastError ?? ''}>
                        {e.lastError ?? '—'}
                      </td>
                      <td className="py-1.5 text-xs text-slate-500">
                        {e.status === 'FAILED' ? 'Not retried' : at(e.nextAttemptAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {h.held.items.length > 0 && (
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Held in CenoX, waiting for an earlier event
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1.5">Reference</th>
                    <th className="py-1.5">Channel</th>
                    <th className="py-1.5">Version</th>
                    <th className="py-1.5">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {h.held.items.map((e) => (
                    <tr key={`${e.externalRef}:${e.version}`}>
                      <td className="py-1.5 font-mono text-xs">{e.externalRef}</td>
                      <td className="py-1.5">{e.originSource}</td>
                      <td className="py-1.5">v{e.version}</td>
                      <td className="py-1.5 text-xs text-slate-500">{at(e.receivedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }): ReactNode {
  return (
    <div className="rounded-lg border border-slate-200 p-2">
      <dt>{label}</dt>
      <dd className="text-base font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
